"use client"

import { fr } from "@codegouvfr/react-dsfr"
import Button from "@codegouvfr/react-dsfr/Button"
import RadioButtons from "@codegouvfr/react-dsfr/RadioButtons"
import { Box } from "@mui/material"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { LBA_ITEM_TYPE } from "shared/constants/lbaitem"
import { isAutoRadiusWidening } from "shared/utils/auto-radius"
import { Footer } from "@/app/_components/Footer"
import DefaultContainer from "@/app/_components/Layout/DefaultContainer"
import { LiveStatus } from "@/app/_components/LiveStatus"
import { zoneScopedId } from "@/app/_components/zone-ids"
import { MATOMO_EVENTS, pushMatomoEvent, SEARCH_ENGINES } from "@/utils/matomo-utils"
import { pluralize } from "@/utils/strutils"
import type { AutoRadiusChain } from "../_hooks/use-auto-radius"
import { autoRadiusTracking, extendAutoRadiusChain, useAutoRadius } from "../_hooks/use-auto-radius"
import { useSearchResults } from "../_hooks/use-search-results"
import type { ISearchPageParams, SearchMode } from "../_utils/search.params.utils"
import { ACTIVE_HIT_PARAM, buildSearchUrl } from "../_utils/search.params.utils"
import type { FilterChange, SortChange } from "../_utils/search.tracking.utils"
import { diffFilterChanges, diffSortChange, searchTypeOf } from "../_utils/search.tracking.utils"
import { parseSearchPageParamsWithLegacy } from "../_utils/search-legacy-utils"
import { SearchBar } from "./SearchBar"
import { clearedFilters, SearchFilters } from "./SearchFilters"
import { SearchMobilePanel } from "./SearchMobilePanel"
import { SearchMobileSummaryBar } from "./SearchMobileSummaryBar"
import { SearchMobileTriModal } from "./SearchMobileTriModal"
import { SearchResultsList } from "./SearchResultsList"
import { SearchSortSelect } from "./SearchSortSelect"
import { SEARCH_MODE_OPTIONS, SearchTypeRechercheSelect } from "./SearchTypeRechercheSelect"

interface SearchPageClientProps {
  initialParams: ISearchPageParams
}

type MobilePanel = null | "search" | "filters" | "tri"

// Identifie une recherche distincte pour la télémétrie : pagination et origine de q exclues.
const searchKeyOf = (p: ISearchPageParams) => {
  const { page: _page, hitsPerPage: _hitsPerPage, q_source: _qSource, ...trackedParams } = p
  return JSON.stringify(trackedParams)
}

export function SearchPageClient({ initialParams }: SearchPageClientProps) {
  const rawSearchParams = useSearchParams()
  const params = rawSearchParams ? parseSearchPageParamsWithLegacy(new URLSearchParams(rawSearchParams.toString())) : initialParams

  const searchKey = searchKeyOf(params)
  const [autoRadiusChain, setAutoRadiusChain] = useState<AutoRadiusChain | null>(null)
  const result = useSearchResults(params)
  const router = useRouter()

  // Retour depuis une fiche détail (?active_hit=…, posé par useDetailNavigation) : capturé dans
  // un state pour survivre au router.replace qui retire le paramètre. Pas de `useState(() => …)`
  // au montage : l'App Router peut réutiliser cette instance depuis son cache de navigation sans
  // la remonter. Pas d'effet non plus (il tournerait après la peinture, d'où un flash) : l'état
  // est ajusté PENDANT le rendu (pattern React « adjusting state during rendering »).
  const rawActiveHit = rawSearchParams?.get(ACTIVE_HIT_PARAM) ?? null
  const [seenActiveHit, setSeenActiveHit] = useState(rawActiveHit)
  const [pendingScrollHitId, setPendingScrollHitId] = useState<string | null>(null)
  if (rawActiveHit !== seenActiveHit) {
    setSeenActiveHit(rawActiveHit)
    if (rawActiveHit) setPendingScrollHitId(rawActiveHit)
  }

  // Résultats disponibles (carte visée rendue ou absente, on ne retente pas) : on retire le
  // paramètre, à usage unique, de l'URL. `scroll: false` impératif : sans lui, ce `replace`
  // ramène la page en haut et efface le scroll qu'on vient de restaurer.
  useEffect(() => {
    if (!pendingScrollHitId || !result.data) return
    setPendingScrollHitId(null)
    router.replace(buildSearchUrl(params), { scroll: false })
    // `params`/`router` volontairement absents des deps : `params` est un objet recréé à
    // chaque rendu (le mettre en dep ferait tourner l'effet en boucle), `router` est stable.
  }, [pendingScrollHitId, result.data])

  const [panel, setPanel] = useState<MobilePanel>(null)
  // Champ en cours de saisie dans la modale « Modifier la recherche » : SearchBar passe en
  // « écran de saisie » (suggestions inline plein écran) — le reste du formulaire est masqué.
  const [searchFieldActive, setSearchFieldActive] = useState(false)
  // Saisie métier pas encore lancée (champ quitté sans Entrée ni sélection) : le bouton
  // Rechercher du panneau la lance, comme celui de la home.
  const pendingQRef = useRef<{ q: string; source: "suggestion" | "free_text" } | null>(null)
  const closeSearchPanel = () => {
    pendingQRef.current = null
    setPanel(null)
    // Fermeture possible pendant une saisie (croix, Escape) : sans reset, la prochaine
    // ouverture masquerait type de recherche et bouton.
    setSearchFieldActive(false)
  }

  // Bandeau desktop collé ou non : sentinelle observée juste au-dessus du sticky — dès
  // qu'elle sort du viewport par le haut, le bandeau est collé (fond blanc pleine largeur).
  const [isStuck, setIsStuck] = useState(false)
  const stickySentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const sentinel = stickySentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting))
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  // Navigation à l'initiative de l'usager : clôt la chaîne d'élargissement en cours, sinon un
  // retour aux params de son dernier palier (filtre coché puis décoché) serait compté comme palier.
  const navigateSilent = useCallback(
    (newParams: ISearchPageParams) => {
      setAutoRadiusChain(null)
      router.replace(buildSearchUrl(newParams))
    },
    [router]
  )

  const nbHits = result.data?.pages.at(-1)?.nbHits ?? 0
  const facets = result.data?.pages[0]?.facets
  const counts = result.data?.pages[0]?.counts

  // Compteur annoncé, commun aux arbres desktop et mobile (RGAA 7.5). Vidé pendant chaque requête
  // (hors « Voir plus », cf. SearchResultsList) : un tri ou une recherche qui donne le même nombre
  // est annoncé à nouveau. Rien tant que l'élargissement automatique du rayon est en cours.
  const isRefreshing = result.isFetching && !result.isFetchingNextPage
  const stillWidening = isAutoRadiusWidening(params, nbHits)
  const resultsStatus = !result.data || isRefreshing || stillWidening ? "" : nbHits === 0 ? "Aucun résultat" : pluralize(nbHits, "résultat")

  // Changements de filtres en attente de leurs compteurs : la spec veut results_count et
  // active_filters_count APRÈS application — on mémorise le diff au clic et on pousse les
  // événements quand les résultats de la nouvelle recherche sont affichés (effet ci-dessous).
  const pendingFilterChangesRef = useRef<{ searchKey: string; changes: FilterChange[] } | null>(null)
  // Changement de tri en attente (même mécanique) : les tris date/candidatures/début de
  // contrat excluent des documents → results_count post-application signifiant.
  const pendingSortChangeRef = useRef<{ searchKey: string; change: SortChange } | null>(null)

  // search_results_displayed : une fois par recherche distincte (q/lieu/mode/filtres/tri),
  // pas à chaque page chargée via « Voir plus » ni à chaque palier d'élargissement du rayon
  // (#5634) — même événement que le legacy, enrichi de search_engine. Les compteurs par type
  // viennent de la facette `type` du result set.
  const lastTrackedSearchKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!result.data) return
    // keepPreviousData : pendant le refetch, result.data appartient à la recherche PRÉCÉDENTE
    // → attendre les données fraîches (sinon compteurs faux sur les événements ci-dessous).
    if (result.isPlaceholderData) return
    // Palier intermédiaire : rien n'est encore affiché à l'usager, les événements attendent le
    // dernier palier (cf. handleAutoRadiusStep, qui y reporte les changements en attente).
    if (stillWidening) return

    // Flush des search_filter_applied/removed de la recherche affichée (compteurs post-application).
    if (pendingFilterChangesRef.current?.searchKey === searchKey) {
      const totalResults = result.data.pages.at(-1)?.nbHits ?? 0
      for (const change of pendingFilterChangesRef.current.changes) {
        pushMatomoEvent({
          event: change.action === "applied" ? MATOMO_EVENTS.SEARCH_FILTER_APPLIED : MATOMO_EVENTS.SEARCH_FILTER_REMOVED,
          filter_name: change.filter_name,
          filter_value: change.filter_value,
          active_filters_count: activeFilterCount,
          results_count: totalResults,
          search_engine: SEARCH_ENGINES.BETA,
        })
      }
      pendingFilterChangesRef.current = null
    }

    // Flush du search_sort_changed de la recherche affichée.
    if (pendingSortChangeRef.current?.searchKey === searchKey) {
      pushMatomoEvent({
        event: MATOMO_EVENTS.SEARCH_SORT_CHANGED,
        ...pendingSortChangeRef.current.change,
        results_count: result.data.pages.at(-1)?.nbHits ?? 0,
        search_engine: SEARCH_ENGINES.BETA,
      })
      pendingSortChangeRef.current = null
    }

    if (lastTrackedSearchKeyRef.current === searchKey) return
    lastTrackedSearchKeyRef.current = searchKey
    pushMatomoEvent({
      event: MATOMO_EVENTS.SEARCH_RESULTS_DISPLAYED,
      total_results: result.data.pages.at(-1)?.nbHits ?? 0,
      count_alternance: facets?.type?.offre ?? 0,
      count_formation: facets?.type?.formation ?? 0,
      // Détail par type d'offre : facette sub_type du result set.
      count_offer_lba: facets?.sub_type?.[LBA_ITEM_TYPE.OFFRES_EMPLOI_LBA] ?? 0,
      count_offer_partner: facets?.sub_type?.[LBA_ITEM_TYPE.OFFRES_EMPLOI_PARTENAIRES] ?? 0,
      count_company_algo: facets?.sub_type?.[LBA_ITEM_TYPE.RECRUTEURS_LBA] ?? 0,
      search_job_name: params.q || "non_renseigné",
      search_address: params.lieu_label || "non_renseigné",
      // Emprise administrative (region:53, departement:44) : distingue une recherche « Bretagne »
      // d'une recherche par point + rayon, que search_address seul ne permet pas.
      search_admin_area: params.admin_area ?? "non_renseigné",
      search_diploma: params.level?.[0] ?? "indifferent",
      ...autoRadiusTracking(autoRadiusChain, searchKey, params.radius),
      search_engine: SEARCH_ENGINES.BETA,
    })
  })
  // NB : effet sans tableau de deps (gardes idempotentes par searchKey) — il doit relire
  // activeFilterCount et le ref pending à chaque affichage de résultats.

  const activeFilterCount =
    (params.type_filter_label?.length ?? 0) +
    (params.contract_type?.length ?? 0) +
    (params.level?.length ?? 0) +
    (params.is_algo_company?.length ?? 0) +
    (params.start_date ? 1 : 0) +
    (params.handi ? 1 : 0) +
    (params.urgent ? 1 : 0) +
    (params.smart_apply ? 1 : 0)

  function handleSearch(q: string, source: "suggestion" | "free_text") {
    // Même événement que les formulaires legacy, enrichi de search_engine et de l'origine
    // de la saisie (suggestion d'autocomplétion vs texte libre).
    pushMatomoEvent({
      event: MATOMO_EVENTS.SEARCH_LAUNCHED,
      search_job_name: q || "non_renseigné",
      search_address: params.lieu_label || "non_renseigné",
      search_admin_area: params.admin_area ?? "non_renseigné",
      search_radius: params.radius,
      search_diploma: params.level?.[0] ?? "indifferent",
      search_origin: "page_resultat",
      search_engine: SEARCH_ENGINES.BETA,
      q_source: source,
    })
    navigateSilent({ ...params, q: q || undefined, q_source: q ? source : undefined, page: 0 })
  }

  function submitSearchPanel() {
    const pending = pendingQRef.current
    const q = pending?.q.trim() ?? ""
    if (pending && q !== (params.q ?? "")) handleSearch(q, pending.source)
    closeSearchPanel()
  }

  function handleLieuChange(lieu: { label: string; latitude: number; longitude: number; adminArea?: string } | null) {
    // Nouveau lieu → on repart du rayon le plus étroit (élargissement auto ensuite).
    if (lieu) {
      navigateSilent({ ...params, lieu_label: lieu.label, latitude: lieu.latitude, longitude: lieu.longitude, admin_area: lieu.adminArea, radius: 20, page: 0 })
    } else {
      navigateSilent({ ...params, lieu_label: undefined, latitude: undefined, longitude: undefined, admin_area: undefined, radius: 20, page: 0 })
    }
  }

  // Changement de type de recherche : les filtres actifs ne s'appliquent plus forcément au
  // nouveau mode (chips différentes) → remis à zéro, comme le tri s'il n'existe pas en mode
  // formations. Le clear implicite n'émet PAS de search_filter_removed (c'est un changement
  // de mode, pas des retraits unitaires).
  function handleModeChange(mode: SearchMode) {
    pushMatomoEvent({ event: MATOMO_EVENTS.SEARCH_TYPE_CHANGED, search_type: searchTypeOf(mode), search_engine: SEARCH_ENGINES.BETA })
    const cleared = clearedFilters(params)
    const sortValidInMode = mode === "formations" ? params.sort === "proximity" : true
    navigateSilent({ ...cleared, mode, sort: sortValidInMode ? params.sort : undefined })
  }

  function handleAutoRadiusStep(radius: number) {
    const newParams = { ...params, radius, page: 0 }
    const newSearchKey = searchKeyOf(newParams)
    setAutoRadiusChain((chain) => extendAutoRadiusChain(chain, { searchKey, radius: params.radius }, newSearchKey))
    // Filtres et tri en attente : poussés avec le results_count du dernier palier, pas du premier.
    if (pendingFilterChangesRef.current?.searchKey === searchKey) pendingFilterChangesRef.current.searchKey = newSearchKey
    if (pendingSortChangeRef.current?.searchKey === searchKey) pendingSortChangeRef.current.searchKey = newSearchKey
    router.replace(buildSearchUrl(newParams))
  }

  useAutoRadius({ params, result, onRadiusChange: handleAutoRadiusStep })

  function handleFilterChange(newParams: ISearchPageParams) {
    // Mémorise le diff de filtres (un événement par valeur modifiée) — poussé avec les
    // compteurs post-application quand les résultats de la nouvelle recherche s'affichent.
    // Changements en rafale avant l'arrivée des résultats : les diffs s'accumulent.
    const changes = diffFilterChanges(params, newParams)
    if (changes.length) {
      pendingFilterChangesRef.current = {
        searchKey: searchKeyOf(newParams),
        changes: [...(pendingFilterChangesRef.current?.changes ?? []), ...changes],
      }
    }
    // Changement de tri (select desktop et modale mobile passent tous deux par ici) : en
    // rafale, seul le dernier état compte — le previous_sort_value du premier diff est conservé.
    const sortChange = diffSortChange(params, newParams)
    if (sortChange) {
      pendingSortChangeRef.current = {
        searchKey: searchKeyOf(newParams),
        change: { ...sortChange, previous_sort_value: pendingSortChangeRef.current?.change.previous_sort_value ?? sortChange.previous_sort_value },
      }
    }
    navigateSilent(newParams)
  }

  return (
    <>
      {/* Panneau mobile ouvert (aria-modal) : il porte sa propre annonce, celle-ci se tait. */}
      <LiveStatus message={panel ? "" : resultsStatus} />
      <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: fr.colors.decisions.background.alt.grey.default, minHeight: "100dvh" }}>
        {/* Desktop : bandeau de recherche + liste mono-colonne (affichage piloté par CSS pour éviter le flash d'hydratation).
            overflow-x clip : le fond blanc 100vw du bandeau collé inclut la scrollbar — clip
            évite le débordement horizontal sans créer de scroll container (sticky préservé). */}
        <Box sx={{ display: { xs: "none", lg: "block" }, flex: 1, overflowX: "clip" }}>
          <DefaultContainer sx={{ py: fr.spacing("4v") }}>
            {/* Bandeau sticky au scroll. z-index > 500 (cartes DSFR shadow), < 1100 (.fr-header,
                cf application.css) et < 1250 (modales DSFR). */}
            <Box ref={stickySentinelRef} aria-hidden="true" sx={{ height: 0 }} />
            <Box sx={{ position: "sticky", top: 0, zIndex: 999 }}>
              {/* Bandeau collé : fond blanc pleine largeur (comme le legacy) — couvre les
                  gouttières latérales du container et masque les résultats qui défilent
                  derrière (encoches des coins arrondis comprises). */}
              {isStuck && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: "50%",
                    width: "100vw",
                    transform: "translateX(-50%)",
                    backgroundColor: fr.colors.decisions.background.default.grey.default,
                    // L'ombre de séparation avec les résultats est portée par la bande
                    // pleine largeur, le panneau collé n'en projette pas.
                    boxShadow: "0 2px 6px rgba(0,0,18,0.08)",
                  }}
                />
              )}
              <Box
                sx={{
                  position: "relative",
                  backgroundColor: fr.colors.decisions.background.default.grey.default,
                  // Collé : coins et ombre latérale du panneau masqués (il se fond dans la
                  // bande pleine largeur, comme le bandeau du legacy).
                  borderRadius: isStuck ? 0 : "8px",
                  p: fr.spacing("6v"),
                  boxShadow: isStuck ? "none" : "0 2px 6px rgba(0,0,18,0.08)",
                }}
              >
                <Box id={zoneScopedId("recherche", "search-form")} tabIndex={-1} sx={{ display: "flex", gap: fr.spacing("3v"), alignItems: "flex-end" }}>
                  <Box sx={{ flex: 1 }}>
                    {/* submitOnSelect/submitOnClear : pas de bouton Rechercher ici, une option métier acceptée
                        (saisie libre ou suggestion) ou un champ vidé s'appliquent aussitôt, comme le lieu. */}
                    <SearchBar
                      mode={params.mode}
                      initialQ={params.q}
                      initialLieuLabel={params.lieu_label}
                      franceEntiereIfEmpty
                      submitOnClear
                      submitOnSelect
                      onSubmit={handleSearch}
                      onLieuChange={handleLieuChange}
                    />
                  </Box>
                  <SearchTypeRechercheSelect value={params.mode} onChange={handleModeChange} />
                </Box>
                <Box sx={{ pt: fr.spacing("4v") }}>
                  <SearchFilters params={params} facets={facets} counts={counts} nbHits={result.data ? nbHits : undefined} onNavigate={handleFilterChange} />
                </Box>
              </Box>
            </Box>

            {/* Ligne tri + compteur */}
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", py: fr.spacing("4v") }}>
              <SearchSortSelect params={params} onNavigate={handleFilterChange} />
              {result.data && (
                <Box sx={{ fontWeight: 700, color: fr.colors.decisions.text.title.grey.default }}>
                  {nbHits} résultat{nbHits > 1 ? "s" : ""}
                </Box>
              )}
            </Box>

            <Box role="region" id={zoneScopedId("recherche", "search-content-container")} tabIndex={-1} aria-label="Résultats de la recherche">
              <SearchResultsList result={result} params={params} scrollToHitId={pendingScrollHitId} />
            </Box>
          </DefaultContainer>
        </Box>

        {/* Mobile : barre résumé (2 lignes + chips Filtres/Tri) et liste plein écran */}
        <Box sx={{ display: { xs: "flex", lg: "none" }, flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box
            id={zoneScopedId("recherche", "search-form-mobile")}
            tabIndex={-1}
            sx={{
              position: "sticky",
              top: 0,
              // > 500 (cartes DSFR shadow), < 1100 (.fr-header, cf application.css), < 1250
              // (modales DSFR) — voir commentaire desktop ci-dessus.
              zIndex: 999,
              flexShrink: 0,
              px: fr.spacing("4v"),
              py: fr.spacing("2v"),
              backgroundColor: fr.colors.decisions.background.default.grey.default,
              borderBottom: `1px solid ${fr.colors.decisions.border.default.grey.default}`,
            }}
          >
            <SearchMobileSummaryBar
              params={params}
              activeFilterCount={activeFilterCount}
              onOpenSearch={() => setPanel("search")}
              onOpenFilters={() => setPanel("filters")}
              onOpenTri={() => setPanel("tri")}
            />
          </Box>

          <Box
            role="region"
            id={zoneScopedId("recherche", "search-content-container-mobile")}
            tabIndex={-1}
            aria-label="Résultats de la recherche"
            sx={{ flex: 1, px: fr.spacing("4v"), py: fr.spacing("2v") }}
          >
            <SearchResultsList result={result} params={params} scrollToHitId={pendingScrollHitId} />
          </Box>
        </Box>

        {panel === "search" && (
          <SearchMobilePanel title="Modifier la recherche" hideHeader={searchFieldActive} onClose={closeSearchPanel}>
            {/* height 100% pendant la saisie : les suggestions inline de SearchBar remplissent
                l'espace du panneau (borné au viewport visible, donc au-dessus du clavier). */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: fr.spacing("4v"), height: searchFieldActive ? "100%" : undefined }}>
              <SearchBar
                mode={params.mode}
                layout="column"
                inlineSuggestions
                onActiveFieldChange={(field) => setSearchFieldActive(field !== null)}
                initialQ={params.q}
                initialLieuLabel={params.lieu_label}
                franceEntiereIfEmpty
                submitOnSelect
                onSubmit={(q, source) => {
                  pendingQRef.current = null
                  handleSearch(q, source)
                }}
                onQChange={(q, source) => {
                  pendingQRef.current = { q, source }
                }}
                onLieuChange={handleLieuChange}
              />
              {/* Masqués pendant la saisie : l'écran est réservé au champ actif + suggestions. */}
              {!searchFieldActive && (
                <>
                  <RadioButtons
                    legend="Type de recherche"
                    options={SEARCH_MODE_OPTIONS.map((option) => ({
                      label: option.label,
                      hintText: option.hint,
                      nativeInputProps: { checked: params.mode === option.value, onChange: () => handleModeChange(option.value) },
                    }))}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: fr.spacing("4v") }}>
                    <Button priority="primary" iconId="fr-icon-search-line" onClick={submitSearchPanel} style={{ width: "100%", justifyContent: "center" }}>
                      Rechercher
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </SearchMobilePanel>
        )}

        {panel === "tri" && <SearchMobileTriModal params={params} onNavigate={handleFilterChange} onClose={() => setPanel(null)} />}

        {panel === "filters" && (
          <SearchMobilePanel
            title="Filtrer les offres"
            onClose={() => setPanel(null)}
            footer={
              <Box sx={{ display: "flex", alignItems: "center", gap: fr.spacing("3v") }}>
                {activeFilterCount > 1 && (
                  <Button priority="tertiary no outline" onClick={() => handleFilterChange(clearedFilters(params))}>
                    Tout effacer
                  </Button>
                )}
                <Button priority="primary" onClick={() => setPanel(null)} style={{ flex: 1, justifyContent: "center" }}>
                  {nbHits > 1 ? `Voir les ${nbHits} résultats` : `Voir ${nbHits} résultat`}
                </Button>
              </Box>
            }
          >
            <SearchFilters
              variant="sections"
              params={params}
              facets={facets}
              counts={counts}
              nbHits={result.data ? nbHits : undefined}
              onNavigate={handleFilterChange}
              onClose={() => setPanel(null)}
            />
            <LiveStatus message={resultsStatus} />
          </SearchMobilePanel>
        )}
      </Box>
      <Footer zone="recherche" />
    </>
  )
}
